"use client"

import { useEffect, useCallback } from "react"
import { Button } from "@/components/ui/button"
import { Slider } from "@/components/ui/slider"
import { Badge } from "@/components/ui/badge"
import { Play, Square, Plugs, SlidersHorizontal, Gauge, MoveVertical, AlignVerticalJustifyCenter } from "@/lib/icons"
import { useDeviceStore } from "@/lib/device-store"
import { deviceController } from "@/lib/device-controller"
import { useManualModeStore } from "@/lib/manual-mode-store"
import { cn } from "@/lib/utils"
import { useTranslations } from "next-intl"

interface Props {
  /** Compact single-column layout for sidebars */
  compact?: boolean
  className?: string
}

export function DeviceManualControls({ compact, className }: Props) {
  const t = useTranslations("deviceManualControls")
  const { getActiveDevice } = useDeviceStore()
  const device = getActiveDevice()
  const { isActive, speed, strokeMin, strokeMax, setActive, setSpeed, setStrokeRange } = useManualModeStore()

  const isPlaying = isActive
  const connected = device?.isConnected ?? false
  // Buttplug devices' real capability isn't knowable from device.type alone
  // (a Handy connected via Intiface is type "buttplug", not "handy", but is
  // just as much a linear/stroke device) -- ask the adapter what the
  // connected hardware actually reports instead of assuming from type.
  const capabilities = connected ? deviceController.getCapabilities() : null
  const hasStrokeControls = device?.type === "handy" || capabilities?.linear === true
  const isPureVibrator = capabilities !== null && capabilities.vibrate && !capabilities.linear

  // Stop device if it disconnects while running
  useEffect(() => {
    if (!connected && isActive) {
      deviceController.manualStop().catch(() => {})
      setActive(false)
    }
  }, [connected, isActive, setActive])

  const handlePlayStop = async () => {
    if (!connected) return
    if (isPlaying) {
      await deviceController.manualStop()
      setActive(false)
    } else {
      const ok = await deviceController.manualStart(speed)
      if (ok) setActive(true)
    }
  }

  const handleSpeedChange = useCallback(async (val: number[]) => {
    const s = val[0]
    setSpeed(s)
    if (isActive) await deviceController.manualSetSpeed(s)
  }, [isActive, setSpeed])

  const handleStrokeMinChange = async (val: number[]) => {
    const v = Math.min(val[0], strokeMax - 5)
    setStrokeRange(v, strokeMax)
    await deviceController.setStrokeRange(v, strokeMax)
  }

  const handleStrokeMaxChange = async (val: number[]) => {
    const v = Math.max(val[0], strokeMin + 5)
    setStrokeRange(strokeMin, v)
    await deviceController.setStrokeRange(strokeMin, v)
  }

  // Single two-thumb slider — bottom/top in one control, used by the compact
  // layout instead of the separate length + position sliders below.
  const handleStrokeRangeChange = async (val: number[]) => {
    const min = Math.min(val[0], val[1] - 5)
    const max = Math.max(val[1], val[0] + 5)
    setStrokeRange(min, max)
    await deviceController.setStrokeRange(min, max)
  }

  const strokeLength = strokeMax - strokeMin
  const handleStrokeLengthChange = async (val: number[]) => {
    const len = val[0]
    const center = (strokeMin + strokeMax) / 2
    const half = len / 2
    const newMin = Math.max(0, Math.round(center - half))
    const newMax = Math.min(100, Math.round(center + half))
    setStrokeRange(newMin, newMax)
    await deviceController.setStrokeRange(newMin, newMax)
  }

  if (!device) {
    return (
      <div className={cn("text-xs text-muted-foreground text-center py-4", className)}>
        {t("noDeviceConnected")}
      </div>
    )
  }

  if (!connected) {
    return (
      <div className={cn("text-xs text-muted-foreground text-center py-4", className)}>
        <Plugs className="h-4 w-4 mx-auto mb-1 opacity-40" />
        {t("nameNotConnected", { name: device.name })}
      </div>
    )
  }

  // Compact layout for the docked chat strip — everything on the fewest rows
  // possible: play/stop, speed, and (Handy only) one two-thumb slider for
  // stroke range instead of the separate length + position sliders below.
  if (compact) {
    return (
      <div className={cn("space-y-2", className)}>
        <div className="flex items-center gap-2">
          <Button
            size="sm"
            className={cn(
              "h-7 shrink-0 gap-1 px-2.5 text-xs",
              isPlaying ? "bg-red-600 hover:bg-red-700 text-white" : "bg-primary hover:bg-primary/90"
            )}
            onClick={handlePlayStop}
          >
            {isPlaying ? <Square className="h-3 w-3" /> : <Play className="h-3 w-3" />}
            {isPlaying ? t("stop") : t("start")}
          </Button>
          <div className="flex min-w-0 flex-1 items-center gap-1.5">
            <Gauge className="h-3 w-3 shrink-0 text-muted-foreground" />
            <Slider value={[speed]} min={0} max={100} step={1} onValueChange={handleSpeedChange} disabled={!connected} className="min-w-0" />
            <span className="w-8 shrink-0 text-right text-[10px] tabular-nums text-muted-foreground">{speed}%</span>
          </div>
        </div>

        {hasStrokeControls && (
          <div className="flex items-center gap-1.5">
            <MoveVertical className="h-3 w-3 shrink-0 text-muted-foreground" />
            <Slider
              value={[strokeMin, strokeMax]}
              min={0}
              max={100}
              step={1}
              onValueChange={handleStrokeRangeChange}
              disabled={!connected}
              className="min-w-0"
            />
            <span className="w-14 shrink-0 text-right text-[10px] tabular-nums text-muted-foreground">
              {strokeMin}-{strokeMax}%
            </span>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={cn("space-y-4", className)}>
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1.5">
          <SlidersHorizontal className="h-3.5 w-3.5 text-primary" />
          <span className="text-sm font-medium">{t("manual")}</span>
        </div>
        <Badge variant={isPlaying ? "default" : "secondary"} className="text-[10px] px-1.5 py-0">
          {isPlaying ? t("active") : t("idle")}
        </Badge>
      </div>

      {/* Play / Stop */}
      <Button
        className={cn(
          "w-full gap-2",
          isPlaying
            ? "bg-red-600 hover:bg-red-700 text-white"
            : "bg-primary hover:bg-primary/90"
        )}
        onClick={handlePlayStop}
      >
        {isPlaying ? (
          <><Square className="h-4 w-4" /> {t("stop")}</>
        ) : (
          <><Play className="h-4 w-4 ml-0.5" /> {t("start")}</>
        )}
      </Button>

      {/* Speed */}
      <div className="space-y-2">
        <div className="flex items-center justify-between text-xs">
          <span className="flex items-center gap-1 text-muted-foreground">
            <Gauge className="h-3 w-3" />
            {t("speed")}
          </span>
          <span className="tabular-nums font-medium">{speed}%</span>
        </div>
        <Slider
          value={[speed]}
          min={0}
          max={100}
          step={1}
          onValueChange={handleSpeedChange}
          disabled={!connected}
        />
        {/* Quick speed presets */}
        <div className="flex gap-1">
          {[25, 50, 75, 100].map((v) => (
            <button
              key={v}
              type="button"
              onClick={() => handleSpeedChange([v])}
              className={cn(
                "flex-1 text-[10px] py-0.5 rounded border transition-colors",
                speed === v
                  ? "bg-primary/20 border-primary/40 text-primary"
                  : "hover:bg-accent border-border"
              )}
            >
              {v}%
            </button>
          ))}
        </div>
      </div>

      {/* Stroke controls — Handy, or any Buttplug device reporting LinearCmd
          (e.g. a Handy connected via Intiface, which is device.type
          "buttplug", not "handy", but is exactly as much a stroke device) */}
      {hasStrokeControls && (
        <>
          {/* Stroke length */}
          <div className="space-y-2">
            <div className="flex items-center justify-between text-xs">
              <span className="flex items-center gap-1 text-muted-foreground">
                <MoveVertical className="h-3 w-3" />
                {t("strokeLength")}
              </span>
              <span className="tabular-nums font-medium">{strokeLength}%</span>
            </div>
            <Slider
              value={[strokeLength]}
              min={5}
              max={100}
              step={1}
              onValueChange={handleStrokeLengthChange}
              disabled={!connected}
            />
          </div>

          {/* Stroke position (top / bottom) */}
          <div className="space-y-3">
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              <AlignVerticalJustifyCenter className="h-3 w-3" />
              {t("strokePosition")}
            </span>

            {/* Visual stroke bar */}
            <div className="relative h-20 w-6 mx-auto bg-muted rounded-full overflow-hidden">
              <div
                className="absolute left-0 right-0 bg-primary/60 rounded-full transition-all"
                style={{
                  bottom: `${strokeMin}%`,
                  height: `${strokeLength}%`,
                }}
              />
            </div>

            <div className="flex gap-3">
              <div className="flex-1 space-y-1">
                <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                  <span>{t("bottom")}</span>
                  <span>{strokeMin}%</span>
                </div>
                <Slider
                  value={[strokeMin]}
                  min={0}
                  max={95}
                  step={1}
                  onValueChange={handleStrokeMinChange}
                  disabled={!connected}
                />
              </div>
              <div className="flex-1 space-y-1">
                <div className="flex items-center justify-between text-[10px] text-muted-foreground">
                  <span>{t("top")}</span>
                  <span>{strokeMax}%</span>
                </div>
                <Slider
                  value={[strokeMax]}
                  min={5}
                  max={100}
                  step={1}
                  onValueChange={handleStrokeMaxChange}
                  disabled={!connected}
                />
              </div>
            </div>
          </div>
        </>
      )}

      {/* Devices with no stroke/length capability just get a label
          clarifying what the single Speed slider above actually controls */}
      {!hasStrokeControls && (
        <p className="text-[10px] text-muted-foreground text-center">
          {isPureVibrator || device.type === "lovense" || device.type === "kiiroo"
            ? "Controls vibration intensity"
            : "Controls oscillation speed"}
        </p>
      )}
    </div>
  )
}
